import moment from 'moment-timezone';
import { SEND_NOW, SMS_OPT_STATUSES } from '@/components/broadcast/constants/smsBroadcast.constants';
import { COST_PER_SMS_USD } from '@/components/automatedSms/sms.constants';
import {
    isValidNorthAmericaPhoneNumber,
    normalizeNorthAmericaPhoneNumber,
} from '@/components/broadcast/utils/sms-utils';

export const getSmsDeliveryTotalCostUsd = ({ numMessages, numRecipients, costPerMessageUsd }) => {
    return parseFloat(((costPerMessageUsd ?? COST_PER_SMS_USD) * (numMessages ?? 0) * (numRecipients ?? 0))
        .toFixed(3));
};

export const getScheduleDate = ({
    timing,
    timingDetails: {
        date,
        time,
        timeZone,
    },
}) => {
    if (timing === SEND_NOW) {
        return `${moment().unix()}`;
    }

    const dateTime = moment.tz(timeZone.value);
    const mDate = moment(date);
    const mTime = moment(time, 'HHmm');

    dateTime.set({
        year: mDate.year(),
        month: mDate.month(),
        date: mDate.date(),
        hour: mTime.hour(),
        minute: mTime.minutes(),
        second: 0,
        millisecond: 0,
    });

    return `${dateTime.unix()}`;
};

export const isValidPhone = (phoneNumber) => {
    const MIN_PHONE_LENGTH = 10;
    const MAX_PHONE_LENGTH = 11;

    if (phoneNumber) {
        const phone = phoneNumber.replace(/\D/g, '');

        return phone.length >= MIN_PHONE_LENGTH && phone.length <= MAX_PHONE_LENGTH;
    }

    return false;
};

export const getValidNumbers = (recipients) => {
    return recipients
        .map(({ phoneNumber }) => phoneNumber)
        .filter((phoneNumber) => {
            return isValidPhone(phoneNumber);
        });
};

export const getValidRecipientCount = ({ optStatuses, recipients }) => getValidRecipients({ optStatuses, recipients }).length;

export const getValidRecipients = ({ optStatuses, recipients }) => {
    const optedOutPhoneNumbers = getOptedOutRecipients(optStatuses);
    const unreachablePhoneNumbers = getUnreachableRecipients(optStatuses);

    return recipients
        .filter(recipientHasValidNumber)
        .filter(filterNumbersFromList(optedOutPhoneNumbers))
        .filter(filterNumbersFromList(unreachablePhoneNumbers))
        .filter(recipientIsNotDuplicate);
};

export const getSortedValidRecipients = ({ optStatuses, recipients }) => {
    const validRecipients = getValidRecipients({ optStatuses, recipients })
        .map((recipient) => {
            return {
                ...recipient,
                phoneNumber: normalizeNorthAmericaPhoneNumber(recipient.phoneNumber),
            };
        });
    const optedInRecipients = getOptedInRecipients({ optStatuses });

    return validRecipients.sort((a, b) => {
        const aOptedIn = optedInRecipients.includes(a.phoneNumber);
        const bOptedIn = optedInRecipients.includes(b.phoneNumber);

        if (aOptedIn === bOptedIn) {
            return 0;
        }

        if (bOptedIn) {
            return 1;
        }

        return -1;
    });
};

export const recipientHasValidNumber = ({ phoneNumber }) => isValidNorthAmericaPhoneNumber(phoneNumber);

export const filterNumbersFromList = (phoneNumbers) => ({ phoneNumber }) => !phoneNumbers.includes(normalizeNorthAmericaPhoneNumber(phoneNumber));

export const recipientIsNotDuplicate = ({ phoneNumber }, index, self) => self.findIndex((r) => (normalizeNorthAmericaPhoneNumber(r.phoneNumber) === normalizeNorthAmericaPhoneNumber(phoneNumber))) === index;

export const getUnreachableRecipientCount = ({ optStatuses }) => getUnreachableRecipients(optStatuses).length;

export const getTotalValidContactCount = ({ optStatuses, recipients }) => {
    const { OPT_OUT } = SMS_OPT_STATUSES;
    const phoneNumbers = getValidNumbers(recipients);
    const uniqueRecipients = getUniqueRecipients(recipients);

    const duplicateCount = recipients.length - uniqueRecipients.length;

    const optOuts = optStatuses.filter(({ statusKind }) => statusKind === OPT_OUT);

    return phoneNumbers.length - optOuts.length - duplicateCount;
};

export const showEditForStoppedBroadcast = (scheduleDate) => {
    const date = moment(scheduleDate);
    const twoMinutesFromNow = moment().add(2, 'minutes');

    return date < twoMinutesFromNow;
};

export const getOptedOutRecipients = (optStatuses) => {
    const { OPT_OUT } = SMS_OPT_STATUSES;

    return optStatuses
        .filter(({ statusKind }) => statusKind === OPT_OUT)
        .map((optStatus) => normalizeNorthAmericaPhoneNumber(optStatus.phoneNumber));
};

export const getUnreachableRecipients = (optStatuses) => {
    const { UNREACHABLE } = SMS_OPT_STATUSES;

    return optStatuses
        .filter(({ statusKind }) => statusKind === UNREACHABLE)
        .map((optStatus) => normalizeNorthAmericaPhoneNumber(optStatus.phoneNumber));
};

export const getValidRecipientsToSchedule = ({ optStatuses, recipients }) => {
    const optedOutRecipients = getOptedOutRecipients(optStatuses);

    const validRecipients = recipients
        .filter((recipient) => !optedOutRecipients.includes(normalizeNorthAmericaPhoneNumber(recipient.phoneNumber)))
        .filter((recipient) => isValidPhone(recipient.phoneNumber));

    return getUniqueRecipients(validRecipients);
};

export const getLongestMergeableFieldLength = (recipients) => Math.max(...(recipients.map((r) => r?.mergeableField?.length)), 0);

export const getUniqueRecipients = (recipients) => {
    return recipients.filter((recipient, index, self) => self.findIndex((t) => (normalizeNorthAmericaPhoneNumber(t.phoneNumber) === normalizeNorthAmericaPhoneNumber(recipient.phoneNumber))) === index);
};

export const getMessageLength = (message, longestMergeableFieldLength = 0) => {
    const mergeFieldOccurences = message.match(/\[\[contact.first_name\]\]/g) || [];
    const mergeFieldLength = '[[contact.first_name]]'.length;
    const mergeFieldOffset = longestMergeableFieldLength > 0
        ? mergeFieldLength - longestMergeableFieldLength
        : 0;

    return {
        length: message.length - mergeFieldOffset * mergeFieldOccurences.length,
        occurences: mergeFieldOccurences.length,
        offset: mergeFieldOffset,
    };
};

export const getStatisticValueText = (value, total) => {
    return value && value !== 0 && total !== 0
        ? `${value} (${Math.round(value / total * 100)}%)`
        : '0';
};

const MERGE_FIELD_PATTERN = /[[{]+.*?[\]}]+/g;

export const getUnsupportedMergeFields = (text, supportedMergeFields) => {
    return [...text.matchAll(MERGE_FIELD_PATTERN)]
        .map((match) => match[0])
        .filter((match) => !supportedMergeFields.includes(match));
};

export const hasUnsupportedMergeField = (text, supportedMergeFields) => {
    const unsupportedMergeFields = getUnsupportedMergeFields(text, supportedMergeFields);

    return unsupportedMergeFields.length > 0;
};

export const getOptedInRecipients = ({ optStatuses }) => {
    const { SINGLE_OPT_IN, DOUBLE_OPT_IN } = SMS_OPT_STATUSES;

    return optStatuses
        .filter(({ statusKind }) => [SINGLE_OPT_IN, DOUBLE_OPT_IN].includes(statusKind))
        .map(({ phoneNumber }) => normalizeNorthAmericaPhoneNumber(phoneNumber));
};

export const getOptedInRecipientCount = ({ optStatuses }) => getOptedInRecipients({ optStatuses }).length;
